/**
 * AdminLayoutSwitcherFooter — drop-up switcher menu layoutu w stopce admin sidebara (ADR-0034).
 *
 * Analog tenant LayoutSwitcherFooter z różnicami:
 *   - API path: /admin/menu/preference zamiast /auth/menu-preference.
 *   - Query keys: ADMIN_* prefix.
 *   - i18n prefix: admin.menuLayoutBuilder zamiast tenant.menuLayoutBuilder.
 *
 * Renderowany w stopce admin sidebara — pokazany TYLKO gdy admin ma ≥2 dostępne
 * layouty (single-layout admin = zero noise). Klik na trigger → dropdown opens
 * UPWARD. Klik na item → silent PUT preference → invalidate active layout → sidebar
 * refetch → render nowego layoutu.
 *
 * **Świadoma duplikacja** z LayoutSwitcherFooter tenant — wyciągnięcie do shared
 * component odłożone do follow-up taska (deferred per CLAUDE.md "minimal changes").
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronUp } from 'lucide-react';

import { useToast } from '@/contexts/ToastContext';
import {
  ACTIVE_ADMIN_MENU_LAYOUT_QUERY_KEY,
  type ActiveAdminMenuLayoutResponse,
  useActiveAdminMenuLayout,
} from '@/hooks/admin-menu/useActiveAdminMenuLayout';
import {
  ADMIN_MENU_PREFERENCE_KEY,
  AVAILABLE_ADMIN_MENU_LAYOUTS_KEY,
  useAvailableAdminMenuLayouts,
} from '@/hooks/admin-menu/useAdminMenuPreference';
import { api } from '@/lib/api';

export function AdminLayoutSwitcherFooter() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();

  const availableQuery = useAvailableAdminMenuLayouts();
  const activeLayoutQuery = useActiveAdminMenuLayout();

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Klik outside → zamyka dropdown.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const setPreferenceMutation = useMutation({
    mutationFn: async (layoutId: string) => {
      const res = await api.put('/admin/menu/preference', { layoutId });
      return res.data;
    },
    onSuccess: () => {
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ADMIN_MENU_PREFERENCE_KEY });
      queryClient.invalidateQueries({ queryKey: ACTIVE_ADMIN_MENU_LAYOUT_QUERY_KEY });
      toast.show({
        variant: 'success',
        title: t('admin.menuLayoutBuilder.layouts.toasts.preferenceSet'),
      });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('admin.menuLayoutBuilder.saveErrorGeneric'),
      });
      // Refetch availability — admin mógł stracić rolę i dlatego mu odmówiono.
      queryClient.invalidateQueries({ queryKey: AVAILABLE_ADMIN_MENU_LAYOUTS_KEY });
    },
  });

  const selectBuiltInMenuMutation = useMutation({
    // PUT { layoutId: null } (NIE DELETE) — jawny wybór "wbudowane menu ITE".
    // DELETE ma inną, zamierzoną semantykę: reset do fallback chain (rola →
    // is_default), który przy menu przypisanym rolą wracał tym samym menu
    // customowym zamiast do wbudowanego — dokładnie ten bug naprawia ta ścieżka.
    mutationFn: async () => {
      const res = await api.put('/admin/menu/preference', { layoutId: null });
      return res.data;
    },
    onSuccess: () => {
      setOpen(false);
      // Samo invalidate nie czyści danych — sidebar mignąłby jeszcze poprzednim
      // custom layoutem do zakończenia refetchu. { layout: null } jest kompletną,
      // znaną z góry odpowiedzią (bez roundtripu), więc primujemy cache jawnie —
      // wzorzec 1:1 z tenantowym Sidebar.tsx.
      queryClient.setQueryData<ActiveAdminMenuLayoutResponse>(ACTIVE_ADMIN_MENU_LAYOUT_QUERY_KEY, {
        layout: null,
      });
      queryClient.invalidateQueries({ queryKey: ADMIN_MENU_PREFERENCE_KEY });
      queryClient.invalidateQueries({ queryKey: ACTIVE_ADMIN_MENU_LAYOUT_QUERY_KEY });
      toast.show({
        variant: 'success',
        title: t('admin.menuLayoutBuilder.layouts.toasts.builtInSelected'),
      });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('admin.menuLayoutBuilder.saveErrorGeneric'),
      });
    },
  });

  const layouts = availableQuery.data?.layouts ?? [];

  // Bez zapisanych layoutów nie ma czego przełączać (zostaje samo wbudowane menu ITE)
  // — switcher nie renderowany. Z ≥1 layoutem lista = „Wbudowane menu ITE" + layouty.
  if (layouts.length < 1) return null;

  // Aktywny stan liczymy z RESOLVERA (co faktycznie renderuje sidebar), nie z GET
  // preferencji + zgadywania po is_default: GET robi innerJoin, więc "wiersz z
  // layout_id=NULL" (jawne wbudowane menu) i "brak wiersza" (brak preferencji) dają
  // identyczną odpowiedź { layoutId: null } — nie do odróżnienia na tym poziomie.
  // Resolver NIE ma tej ambiguacji (Level 0 kończy się na { layout: null } tylko dla
  // jawnego wyboru), więc to jedyne poprawne źródło prawdy dla ptaszka w przełączniku.
  const activeLayout = activeLayoutQuery.data?.layout ?? null;
  const builtInActive = activeLayout === null;

  const builtInLabel = t('admin.menuLayoutBuilder.layouts.switcher.builtIn');
  const activeName = activeLayout?.name ?? builtInLabel;

  return (
    <div
      ref={containerRef}
      className="relative border-t border-border-secondary px-3 py-2"
      data-testid="admin-layout-switcher-footer"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-md border border-border-secondary bg-bg-primary px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
        data-testid="admin-layout-switcher-trigger"
        aria-label={t('admin.menuLayoutBuilder.layouts.switcher.label')}
        aria-expanded={open}
      >
        <span className="truncate text-left">
          <span className="block text-xs text-text-tertiary">
            {t('admin.menuLayoutBuilder.layouts.switcher.label')}
          </span>
          <span className="block font-medium">{activeName}</span>
        </span>
        <ChevronUp
          className={`h-4 w-4 flex-shrink-0 text-text-tertiary transition-transform ${
            open ? '' : 'rotate-180'
          }`}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          className="absolute bottom-full left-3 right-3 z-40 mb-1 rounded-md border border-border-secondary bg-bg-elevated py-1 shadow-lg"
          data-testid="admin-layout-switcher-dropdown"
        >
          <button
            type="button"
            onClick={() => selectBuiltInMenuMutation.mutate()}
            disabled={selectBuiltInMenuMutation.isPending}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-text-primary hover:bg-bg-secondary disabled:opacity-50"
            data-testid="admin-layout-switcher-builtin"
          >
            {builtInActive ? (
              <Check className="h-3.5 w-3.5 flex-shrink-0 text-accent-600" />
            ) : (
              <span className="w-3.5 flex-shrink-0" />
            )}
            <span>{builtInLabel}</span>
          </button>
          <div className="my-1 border-t border-border-secondary" />
          {layouts.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => setPreferenceMutation.mutate(l.id)}
              disabled={setPreferenceMutation.isPending}
              className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm text-text-primary hover:bg-bg-secondary disabled:opacity-50"
              data-testid={`admin-layout-switcher-item-${l.id}`}
            >
              <span className="flex items-center gap-2">
                {l.id === activeLayout?.id ? (
                  <Check className="h-3.5 w-3.5 text-accent-600" />
                ) : (
                  <span className="w-3.5" />
                )}
                <span>{l.name}</span>
              </span>
              {l.isDefault ? (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                  {t('admin.menuLayoutBuilder.layouts.isDefaultBadge')}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
